import React, { useState, useEffect } from 'react';

function AddBlockDialog({ 
  user, 
  selectedDate, 
  selectedTimeSlot, 
  selectedTeam,
  teams,
  onSave, 
  onCancel 
}) {
  const [formData, setFormData] = useState({
    datum: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    uhrzeit: selectedTimeSlot || '08:00',
    dauer: '60',
    nachricht: '',
    teamId: selectedTeam || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Zeitslots von 08:00 bis 22:00 in 15-Minuten-Schritten generieren
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 21; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:15`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
      slots.push(`${hour.toString().padStart(2, '0')}:45`);
    }
    slots.push('22:00');
    return slots;
  };

  // Terminlängen in 15-Minuten-Schritten generieren (15 Minuten bis 8 Stunden)
  const generateDurationOptions = () => {
    const durations = [];
    for (let minutes = 15; minutes <= 480; minutes += 15) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      let label = '';
      if (hours > 0) {
        label += `${hours}h`;
        if (remainingMinutes > 0) {
          label += ` ${remainingMinutes}min`;
        }
      } else {
        label = `${remainingMinutes} min`;
      }

      durations.push({
        value: minutes.toString(),
        label: label
      });
    }
    return durations;
  };

  // Endzeit berechnen für Anzeige
  const calculateEndTime = () => {
    if (!formData.uhrzeit || !formData.dauer) return '';

    const [hours, minutes] = formData.uhrzeit.split(':').map(Number);
    const durationMinutes = parseInt(formData.dauer);

    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

    return endDate.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Block-Daten vorbereiten
      const blockData = {
        ...formData,
        type: 'block' // Kennzeichnung als Block
      };

      await onSave(blockData);
    } catch (err) {
      setError('Fehler beim Erstellen des Blocks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const durationOptions = generateDurationOptions();

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Zeit blockieren</h2>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-field">
              <label>Datum *</label>
              <input
                type="date"
                name="datum"
                value={formData.datum}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-field">
              <label>Projekt *</label>
              <select
                name="teamId"
                value={formData.teamId}
                onChange={handleChange}
                required
              >
                <option value="">Team auswählen</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Startzeit *</label>
              <select
                name="uhrzeit"
                value={formData.uhrzeit}
                onChange={handleChange}
                required
              >
                {generateTimeSlots().map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Dauer *</label>
              <select
                name="dauer"
                value={formData.dauer}
                onChange={handleChange}
                required
              >
                {durationOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Endzeit (automatisch)</label>
              <div className="calculated-time">
                {calculateEndTime() || 'Startzeit wählen'}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label>Nachricht (optional)</label>
              <textarea
                name="nachricht"
                value={formData.nachricht}
                onChange={handleChange}
                rows={3}
                placeholder="Grund für die Blockierung (z.B. 'Mittagspause', 'Team-Meeting', 'Urlaub')..."
                maxLength={200}
              />
              <div className="char-count">
                {formData.nachricht.length}/200 Zeichen
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Abbrechen
            </button>
            <button type="submit" disabled={loading} className="block-button">
              {loading ? 'Wird erstellt...' : 'Zeit blockieren'}
            </button>
          </div>
        </form>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .modal-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            width: 100%;
            max-width: 500px;
            max-height: 90vh;
            overflow-y: auto;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 20px 0 20px;
            border-bottom: 1px solid #e2e8f0;
            margin-bottom: 20px;
          }

          .modal-header h2 {
            margin: 0;
            color: #2d3748;
            font-size: 1.5rem;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #a0aec0;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: background-color 0.2s;
          }

          .close-button:hover {
            background: #f7fafc;
            color: #4a5568;
          }

          .error-message {
            background: #fed7d7;
            color: #c53030;
            padding: 12px;
            border-radius: 4px;
            margin: 0 20px 20px 20px;
            border: 1px solid #feb2b2;
          }

          form {
            padding: 0 20px 20px 20px;
          }

          .form-row {
            display: flex;
            gap: 15px;
            margin-bottom: 20px;
          }

          .form-field {
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .form-field.full-width {
            flex: 1;
          }

          .form-field label {
            margin-bottom: 5px;
            font-weight: 500;
            color: #4a5568;
            font-size: 0.9rem;
          }

          .form-field input,
          .form-field select,
          .form-field textarea {
            padding: 10px 12px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            font-size: 0.9rem;
            transition: border-color 0.2s, box-shadow 0.2s;
          }

          .form-field input:focus,
          .form-field select:focus,
          .form-field textarea:focus {
            outline: none;
            border-color: #e53e3e;
            box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
          }

          .form-field textarea {
            resize: vertical;
            min-height: 80px;
          }

          .calculated-time {
            padding: 10px 12px;
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            color: #4a5568;
            font-size: 0.9rem;
            min-height: 42px;
            display: flex;
            align-items: center;
          }

          .char-count {
            font-size: 0.8rem;
            color: #a0aec0;
            text-align: right;
            margin-top: 4px;
          }

          .modal-actions {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            margin-top: 30px;
          }

          .cancel-button {
            padding: 10px 20px;
            border: 1px solid #e2e8f0;
            background: white;
            color: #4a5568;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.9rem;
            transition: all 0.2s;
          }

          .cancel-button:hover {
            background: #f7fafc;
            border-color: #cbd5e0;
          }

          .block-button {
            padding: 10px 20px;
            border: none;
            background: #e53e3e;
            color: white;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.9rem;
            font-weight: 500;
            transition: background-color 0.2s;
          }

          .block-button:hover:not(:disabled) {
            background: #c53030;
          }

          .block-button:disabled {
            background: #a0aec0;
            cursor: not-allowed;
          }

          @media (max-width: 768px) {
            .modal-content {
              max-width: 95vw;
              margin: 10px;
            }

            .form-row {
              flex-direction: column;
              gap: 15px;
            }

            .modal-actions {
              flex-direction: column;
            }

            .cancel-button,
            .block-button {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default AddBlockDialog; 